import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from './ProjectProvider';
import { 
  ArrowLeft, 
  Clock, 
  Star, 
  CheckCircle, 
  Download,
  Image as ImageIcon,
  File,
  Lightbulb,
  Palette,
  BookOpen,
  Target,
  Award,
  Zap,
  ChevronLeft,
  ChevronRight,
  X,
  Camera,
  Globe,
  Calendar,
  Users,
  Package,
  FileText,
  Sparkles,
  Info
} from 'lucide-react';

const ProjectView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getProject } = useProjects();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (id) {
      const projectData = getProject(id);
      if (projectData) {
        setProject(projectData);
      } else {
        navigate('/projects');
      }
      setLoading(false);
    }
  }, [id, getProject, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-gray-400 font-medium">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-gray-100 mb-3">Project Not Found</h2>
          <p className="text-slate-600 dark:text-gray-400 mb-8">The project you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/projects')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors shadow-lg"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const images = project.images || [];
  const allImages = project.mainImageUrl ? [
    { id: 'main', url: project.mainImageUrl, caption: 'Main project image' },
    ...images
  ] : images;

  const getDifficultyConfig = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return { 
          color: 'from-emerald-500 to-emerald-600', 
          bg: 'bg-emerald-50', 
          text: 'text-emerald-700',
          icon: Target 
        };
      case 'intermediate':
        return { 
          color: 'from-amber-500 to-amber-600', 
          bg: 'bg-amber-50', 
          text: 'text-amber-700',
          icon: Zap 
        };
      case 'advanced':
        return { 
          color: 'from-red-500 to-red-600', 
          bg: 'bg-red-50', 
          text: 'text-red-700',
          icon: Award 
        };
      default:
        return { 
          color: 'from-slate-500 to-slate-600', 
          bg: 'bg-slate-50', 
          text: 'text-slate-700',
          icon: Star 
        };
    }
  };

  const difficultyConfig = getDifficultyConfig(project.difficulty);
  const DifficultyIcon = difficultyConfig.icon;

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-slate-200/50 dark:border-gray-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-2 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-100 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Projects</span>
            </button>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
                <Globe className="w-4 h-4" />
                <span>Public View</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Project Image */}
            <div className="relative group">
              <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl overflow-hidden shadow-2xl">
                {project.mainImageUrl ? (
                  <img
                    src={project.mainImageUrl}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 cursor-pointer"
                    onClick={() => handleImageClick(0)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-white dark:bg-gray-700 rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                        <ImageIcon className="w-12 h-12 text-slate-400 dark:text-gray-500" />
                      </div>
                      <p className="text-slate-500 dark:text-gray-400 font-medium">No image available</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Image Gallery Indicator */}
              {allImages.length > 1 && (
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-black/60 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white">
                      <Camera className="w-4 h-4" />
                      <span className="text-sm font-medium">{allImages.length} images</span>
                    </div>
                    <button
                      onClick={() => handleImageClick(0)}
                      className="text-white hover:text-blue-300 transition-colors"
                    >
                      <span className="text-sm">View Gallery</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Project Info */}
            <div className="space-y-8">
              {/* Categories & Difficulty */}
              <div className="flex flex-wrap items-center gap-3">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${difficultyConfig.bg} ${difficultyConfig.text} font-semibold`}>
                  <DifficultyIcon className="w-4 h-4" />
                  <span>{project.difficulty}</span>
                </div>
                
                {project.category && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold">
                    <Sparkles className="w-4 h-4" />
                    <span>{project.category}</span>
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-gray-100 leading-tight mb-6">
                  {project.name}
                </h1>
                
                <p className="text-xl text-slate-600 dark:text-gray-300 leading-relaxed">
                  {project.description}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                {project.estimatedTime && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 font-medium">Duration</p>
                        <p className="text-lg font-bold text-slate-900">{project.estimatedTime}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Skill Level</p>
                      <p className="text-lg font-bold text-slate-900">{project.difficulty}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-slate-200 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-12">
            
            {/* Materials Section */}
            {project.materials && project.materials.length > 0 && (
              <section className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-slate-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Materials & Supplies</h2>
                    <p className="text-slate-600">Everything you'll need for this project</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.materials.filter(material => material.trim()).map((material, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-slate-800 font-medium">{material}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Instructions Section */}
            {project.instructions && (
              <section className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-slate-100 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Step-by-Step Instructions</h2>
                    <p className="text-slate-600">Follow these detailed steps to complete your project</p>
                  </div>
                </div>
                
                <div className="prose prose-lg max-w-none">
                  <div className="text-slate-700 leading-relaxed whitespace-pre-line">
                    {project.instructions}
                  </div>
                </div>
              </section>
            )}

            {/* Tips Section */}
            {project.tips && (
              <section className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-3xl p-8 border border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-amber-900">Pro Tips</h2>
                    <p className="text-amber-700">Expert advice to help you succeed</p>
                  </div>
                </div>
                
                <div className="bg-white/60 dark:bg-gray-800/60 rounded-2xl p-6">
                  <div className="text-amber-900 leading-relaxed whitespace-pre-line">
                    {project.tips}
                  </div>
                </div>
              </section>
            )}

            {/* Variations Section */}
            {project.variations && (
              <section className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-3xl p-8 border border-purple-200 dark:border-purple-700">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Palette className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-purple-900">Creative Variations</h2>
                    <p className="text-purple-700">Ideas to customize and make it your own</p>
                  </div>
                </div>
                
                <div className="bg-white/60 dark:bg-gray-800/60 rounded-2xl p-6">
                  <div className="text-purple-900 leading-relaxed whitespace-pre-line">
                    {project.variations}
                  </div>
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-slate-100 dark:border-gray-700 sticky top-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Info className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Project Details</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-gray-700">
                  <span className="text-slate-600 font-medium">Difficulty</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${difficultyConfig.bg} ${difficultyConfig.text}`}>
                    {project.difficulty}
                  </span>
                </div>
                
                {project.category && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-gray-700">
                    <span className="text-slate-600 font-medium">Category</span>
                    <span className="text-slate-900 font-semibold">{project.category}</span>
                  </div>
                )}
                
                {project.estimatedTime && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-gray-700">
                    <span className="text-slate-600 font-medium">Duration</span>
                    <span className="text-slate-900 font-semibold">{project.estimatedTime}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600 font-medium">Materials</span>
                  <span className="text-slate-900 font-semibold">
                    {project.materials ? project.materials.filter(m => m.trim()).length : 0} items
                  </span>
                </div>
              </div>
            </div>

            {/* Project Files */}
            {project.files && project.files.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Download className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Downloads</h3>
                </div>
                
                <div className="space-y-3">
                  {project.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <File className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{file.name}</p>
                          <p className="text-xs text-slate-500">{file.type} • {file.size}</p>
                        </div>
                      </div>
                      <button className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && allImages.length > 0 && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-5xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            {allImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={allImages[selectedImageIndex]?.url}
                alt={allImages[selectedImageIndex]?.caption || 'Project image'}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              {allImages[selectedImageIndex]?.caption && (
                <div className="p-6 bg-slate-50 border-t border-slate-200">
                  <p className="text-slate-700 font-medium">{allImages[selectedImageIndex].caption}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;